import { Card, ST, NI } from "../ui/SharedUI";
import { makeId } from "../utils";

const ACCENT = "#1A5A8A";

export default function LoanLedger({ loans, setLoans, months }) {
  const addLoan = () => {
    const id = makeId("loan");
    setLoans((prev) => [
      ...prev,
      {
        id,
        name: "新規借入",
        type: "equal_payment",
        balance: 0,
        monthlyPayment: 0,
        monthlyPrincipal: 0,
        annualRate: 0,
        startMonth: months[0],
        endMonth: months[months.length - 1],
      },
    ]);
  };

  const removeLoan = (id) => {
    if (!window.confirm("この借入を削除しますか？")) return;
    setLoans((prev) => prev.filter((l) => l.id !== id));
  };

  const updateLoan = (id, key, val) => {
    setLoans((prev) => prev.map((l) => (l.id === id ? { ...l, [key]: val } : l)));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Card>
        <ST>借入台帳</ST>
        <p style={{ fontSize: "11px", color: "#888", marginBottom: "16px", fontFamily: "'Noto Sans JP',sans-serif" }}>
          借入ごとに返済情報を入力してください。元本返済＋利息がCFシミュの出金に、利息が経費の「借入利息」に自動反映されます。
        </p>

        {loans.length === 0 && (
          <div style={{ padding: "24px", textAlign: "center", color: "#bbb", fontSize: "13px", fontFamily: "'Noto Sans JP',sans-serif" }}>
            借入がありません。下のボタンから追加してください。
          </div>
        )}

        {loans.map((loan) => {
          const monthlySchedule = calcMonthlySchedule(loan, months);

          return (
            <div key={loan.id} style={{ border: "1px solid #C8D8EA", borderRadius: "10px", padding: "16px", marginBottom: "16px", background: "#F5F8FC" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
                <input
                  value={loan.name}
                  onChange={(e) => updateLoan(loan.id, "name", e.target.value)}
                  style={{ fontSize: "14px", fontWeight: "700", color: ACCENT, border: "1px solid #C8D8EA", borderRadius: "4px", padding: "4px 10px", fontFamily: "'Noto Sans JP',sans-serif", background: "#fff", flex: 1 }}
                />
                <button
                  onClick={() => removeLoan(loan.id)}
                  style={{ fontSize: "11px", color: "#ccc", background: "none", border: "none", cursor: "pointer", padding: "2px 6px" }}
                  onMouseEnter={(e) => (e.target.style.color = "#C0392B")}
                  onMouseLeave={(e) => (e.target.style.color = "#ccc")}
                  title="この借入を削除"
                >✕</button>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                <span style={{ fontSize: "11px", color: "#666", fontFamily: "'Noto Sans JP',sans-serif", minWidth: "72px" }}>返済方式</span>
                {[
                  { value: "equal_payment", label: "元利均等" },
                  { value: "equal_principal", label: "元金均等" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => updateLoan(loan.id, "type", opt.value)}
                    style={{
                      fontSize: "11px", padding: "4px 12px",
                      background: loan.type === opt.value ? ACCENT : "#fff",
                      color: loan.type === opt.value ? "#fff" : "#666",
                      border: `1px solid ${loan.type === opt.value ? ACCENT : "#D0D8D0"}`,
                      borderRadius: "4px", cursor: "pointer", fontFamily: "'Noto Sans JP',sans-serif",
                    }}
                  >{opt.label}</button>
                ))}
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "12px" }}>
                <LoanField label="現在の借入残高" unit="円">
                  <NI value={loan.balance} onChange={(v) => updateLoan(loan.id, "balance", Number(v))} w="120px" />
                </LoanField>

                {loan.type === "equal_payment" ? (
                  <>
                    <LoanField label="月次返済額（元利合計）" unit="円">
                      <NI value={loan.monthlyPayment} onChange={(v) => updateLoan(loan.id, "monthlyPayment", Number(v))} w="120px" />
                    </LoanField>
                    <LoanField label="年利" unit="%">
                      <input
                        type="number"
                        step="0.1"
                        value={loan.annualRate}
                        onChange={(e) => updateLoan(loan.id, "annualRate", parseFloat(e.target.value) || 0)}
                        style={{ width: "72px", padding: "5px 8px", border: "1px solid #D0D8D0", borderRadius: "6px", fontSize: "12px", fontFamily: "monospace", textAlign: "right" }}
                      />
                    </LoanField>
                  </>
                ) : (
                  <>
                    <LoanField label="月次元本返済額" unit="円">
                      <NI value={loan.monthlyPrincipal} onChange={(v) => updateLoan(loan.id, "monthlyPrincipal", Number(v))} w="120px" />
                    </LoanField>
                    <LoanField label="年利" unit="%">
                      <input
                        type="number"
                        step="0.1"
                        value={loan.annualRate}
                        onChange={(e) => updateLoan(loan.id, "annualRate", parseFloat(e.target.value) || 0)}
                        style={{ width: "72px", padding: "5px 8px", border: "1px solid #D0D8D0", borderRadius: "6px", fontSize: "12px", fontFamily: "monospace", textAlign: "right" }}
                      />
                    </LoanField>
                  </>
                )}

                <LoanField label="返済開始月">
                  <select
                    value={loan.startMonth}
                    onChange={(e) => updateLoan(loan.id, "startMonth", e.target.value)}
                    style={{ padding: "5px 8px", border: "1px solid #D0D8D0", borderRadius: "6px", fontSize: "12px", fontFamily: "'Noto Sans JP',sans-serif" }}
                  >
                    {months.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </LoanField>

                <LoanField label="返済終了月">
                  <select
                    value={loan.endMonth}
                    onChange={(e) => updateLoan(loan.id, "endMonth", e.target.value)}
                    style={{ padding: "5px 8px", border: "1px solid #D0D8D0", borderRadius: "6px", fontSize: "12px", fontFamily: "'Noto Sans JP',sans-serif" }}
                  >
                    {months.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </LoanField>
              </div>

              {loan.balance > 0 && (
                <div style={{ overflowX: "auto", marginTop: "8px" }}>
                  <div style={{ fontSize: "11px", color: ACCENT, fontWeight: "700", marginBottom: "6px", fontFamily: "'Noto Sans JP',sans-serif" }}>月次返済スケジュール（プレビュー）</div>
                  <table style={{ borderCollapse: "collapse", fontSize: "11px", minWidth: "100%" }}>
                    <thead>
                      <tr style={{ borderBottom: "1px solid #C8D8EA" }}>
                        <th style={{ textAlign: "left", padding: "3px 8px", color: "#888" }}>項目</th>
                        {months.map((m) => <th key={m} style={{ textAlign: "right", padding: "3px 6px", color: "#888", minWidth: "60px" }}>{m}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: "元本返済", key: "principal" },
                        { label: "利息", key: "interest" },
                        { label: "合計出金", key: "total", bold: true },
                        { label: "借入残高", key: "balance", color: ACCENT },
                      ].map((row) => (
                        <tr key={row.key} style={{ borderBottom: "1px solid #EEF2F8" }}>
                          <td style={{ padding: "3px 8px", fontFamily: "'Noto Sans JP',sans-serif", fontWeight: row.bold ? "700" : "400", color: row.color || "#444" }}>{row.label}</td>
                          {months.map((m) => (
                            <td key={m} style={{ padding: "3px 6px", textAlign: "right", fontFamily: "monospace", fontWeight: row.bold ? "700" : "400", color: row.key === "balance" ? ACCENT : row.key === "total" ? "#C0392B" : "#444" }}>
                              {monthlySchedule[m]?.[row.key]?.toLocaleString() ?? "—"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}

        <button
          onClick={addLoan}
          style={{ fontSize: "12px", color: ACCENT, background: "none", border: `1px dashed ${ACCENT}`, borderRadius: "6px", cursor: "pointer", padding: "6px 18px", fontFamily: "'Noto Sans JP',sans-serif" }}
        >
          ＋ 借入を追加
        </button>
      </Card>
    </div>
  );
}

export function calcMonthlySchedule(loan, months) {
  const schedule = {};
  let currentBalance = Number(loan.balance) || 0;
  const startIdx = months.indexOf(loan.startMonth);
  const endIdx = months.indexOf(loan.endMonth);

  months.forEach((m, i) => {
    const inRange = startIdx <= endIdx
      ? i >= startIdx && i <= endIdx
      : false;

    if (!inRange || currentBalance <= 0) {
      schedule[m] = { principal: 0, interest: 0, total: 0, balance: currentBalance };
      return;
    }

    let principal = 0;
    let interest = 0;

    if (loan.type === "equal_payment") {
      const total = Number(loan.monthlyPayment) || 0;
      interest = Math.round(currentBalance * (Number(loan.annualRate) || 0) / 100 / 12);
      principal = Math.max(0, Math.min(currentBalance, total - interest));
      const actualTotal = Math.min(total, principal + interest);
      currentBalance = Math.max(0, currentBalance - principal);
      schedule[m] = { principal, interest, total: actualTotal, balance: currentBalance };
    } else {
      principal = Math.min(currentBalance, Number(loan.monthlyPrincipal) || 0);
      interest = Math.round(currentBalance * (Number(loan.annualRate) || 0) / 100 / 12);
      const total = principal + interest;
      currentBalance = Math.max(0, currentBalance - principal);
      schedule[m] = { principal, interest, total, balance: currentBalance };
    }
  });

  return schedule;
}

function LoanField({ label, unit, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <span style={{ fontSize: "10px", color: "#888", fontFamily: "'Noto Sans JP',sans-serif" }}>
        {label}{unit ? `（${unit}）` : ""}
      </span>
      {children}
    </div>
  );
}
